import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EyeOff,
  KeyRound,
  Lock,
  LogOut,
  Save,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { HeroChip } from '@/components/ui/HeroChip';
import { Input } from '@/components/ui/Input';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { cn } from '@/lib/utils';
import { authApi } from '../api/auth.api';
import { PasswordStrength } from '../components/PasswordStrength';
import { SecurityGraphic } from '../components/SecurityGraphic';
import { useAuthStore } from '../store/auth.store';
import { changePasswordSchema, type ChangePasswordValues } from '../schemas/password.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/** The console's panel, used by every block on this screen. */
const PANEL =
  'rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]';

/**
 * What actually happens when the form is submitted.
 *
 * Spelled out beside the fields rather than in a sentence under the heading,
 * because "you will be signed out everywhere" is the part people are surprised
 * by, and a surprise after the fact reads as a bug.
 */
const CONSEQUENCES = [
  {
    icon: LogOut,
    title: 'Every session ends',
    body: 'Phones, tablets, the machine in the office — all of them, including this one.',
  },
  {
    icon: UserCheck,
    title: 'You sign in again',
    body: 'With the new password, on this device, straight after saving.',
  },
  {
    icon: EyeOff,
    title: 'Nobody can read it',
    body: 'Not us, not your administrator. It is stored one-way and never shown again.',
  },
];

/**
 * Changing your own password from inside the app.
 *
 * The server ends every session when the password changes, this one included —
 * so rather than leave the user on a screen whose token has just died, we clear
 * the session here and send them back to sign in with the new password.
 *
 * Drawn as a form beside a running commentary rather than as a bare panel: the
 * rules are checked off as they are typed instead of being listed once above the
 * field and re-read as an error afterwards, and what the save is about to do is
 * on screen before it happens.
 */
export function ChangePasswordPage() {
  const navigate = useNavigate();
  const clearSession = useAuthStore((state) => state.clearSession);
  const user = useAuthStore((state) => state.user);
  /** Set when support or an administrator chose this password for them. */
  const mustChange = useAuthStore((state) => state.user?.mustChangePassword);

  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);

  // Watched so the meter and the checklist move as the field is typed into.
  const newPassword = watch('newPassword') ?? '';
  const confirmPassword = watch('confirmPassword') ?? '';

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.changePassword(values);
      clearSession();
      navigate('/login', { replace: true, state: { passwordChanged: true } });
    } catch (error) {
      if (error instanceof ApiRequestError) {
        applyFieldErrors(setError, error);
        setFormError(error.message);
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  return (
    <div className={cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE)}>
      {/* Banner: the key to one account, over the console's blue wash. */}
      <div className="relative overflow-hidden rounded-3xl border border-blue-100 dark:border-blue-900/40 bg-[#eff6ff] dark:bg-[#172554]/40 px-5 py-3 sm:px-7 sm:py-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]">
        {/* Two lights drifting out of step behind the wash, and a single pass of
            light across it. All decoration, all held still under
            prefers-reduced-motion. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 -top-28 h-64 w-64 rounded-full bg-gradient-to-br from-blue-400/30 to-indigo-500/20 blur-3xl animate-orb"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-28 right-1/4 h-56 w-56 rounded-full bg-gradient-to-br from-sky-300/25 to-blue-400/10 blur-3xl animate-orb-slow"
        />
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen dark:via-white/10" />
        </div>

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <span
              className="relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl border border-blue-200/70 dark:border-blue-800/50 bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-blue-500/25 sm:h-[72px] sm:w-[72px]"
              aria-hidden
            >
              <KeyRound className="relative h-8 w-8" strokeWidth={1.9} />
              <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
            </span>

            <div className="min-w-0">
              <span className="text-[11px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                Your account
              </span>
              <h1 className="mt-0.5 text-xl font-black tracking-tight text-slate-900 dark:text-white sm:text-2xl">
                Change password
              </h1>
              <p className="mt-1 max-w-xl text-xs font-medium text-slate-600 dark:text-slate-300 sm:text-sm">
                Choose a new one for your own account. You will be signed out everywhere and asked
                to sign in again with it.
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {user?.fullName && <HeroChip icon={UserCheck}>{user.fullName}</HeroChip>}
                <HeroChip icon={LogOut}>Signs you out everywhere</HeroChip>
                <HeroChip icon={EyeOff}>Never shown to anyone</HeroChip>
              </div>
            </div>
          </div>

          <div className="hidden w-[180px] shrink-0 lg:block xl:w-[210px]">
            <SecurityGraphic />
          </div>
        </div>
      </div>

      {mustChange && (
        <Alert tone="warning" title="Choose your own password to continue">
          This password was set for you by someone else, so it is known to more than one person.
          Replace it now and the rest of the app opens up.
        </Alert>
      )}

      {formError && (
        <Alert tone="critical" title="Could not change your password">
          {formError}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 animate-slide-down delay-100">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5 xl:col-span-7">
          <div className={cn(PANEL, 'flex flex-col gap-5 p-5 sm:p-6')}>
            <header className="flex items-center gap-3">
              <span
                className="relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-sm"
                aria-hidden
              >
                <Lock className="relative h-[18px] w-[18px]" />
                <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-black tracking-tight text-ink">Your new password</h2>
                <p className="mt-0.5 text-xs font-medium text-ink-3">
                  Type it twice. The checks beside this update as you go.
                </p>
              </div>
            </header>

            <fieldset disabled={isSubmitting} className="contents">
              <div className="flex flex-col gap-4">
                <Input
                  label="Current password"
                  autoComplete="current-password"
                  isPassword
                  leftIcon={<Lock className="h-4 w-4" />}
                  hint="The one you signed in with just now."
                  error={errors.currentPassword?.message}
                  {...register('currentPassword')}
                />

                {/* A rule of its own between the old and the new, so the two
                    halves of this form do not read as one list of three boxes. */}
                <div className="flex items-center gap-3" aria-hidden>
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-ink-3">
                    Replace it with
                  </span>
                  <span className="h-px flex-1 bg-line" />
                </div>

                <Input
                  label="New password"
                  autoComplete="new-password"
                  isPassword
                  leftIcon={<KeyRound className="h-4 w-4" />}
                  error={errors.newPassword?.message}
                  {...register('newPassword')}
                />
                <Input
                  label="Confirm new password"
                  autoComplete="new-password"
                  isPassword
                  leftIcon={<ShieldCheck className="h-4 w-4" />}
                  error={errors.confirmPassword?.message}
                  {...register('confirmPassword')}
                />
              </div>
            </fieldset>

            {/* On a phone the rail below is a long scroll away, so the checks
                come with the fields there and the rail keeps them on a desktop. */}
            <div className="rounded-2xl border border-line bg-surface-2 p-4 xl:hidden">
              <PasswordStrength value={newPassword} confirmation={confirmPassword} />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
              {/* No way out while the change is compulsory — cancelling would
                  only land them back here. */}
              {mustChange ? (
                <span className="text-[11.5px] font-bold text-warn">Required before you go on</span>
              ) : (
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex cursor-pointer items-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-500/30 transition-all hover:from-blue-700 hover:to-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span>Saving…</span>
                ) : (
                  <>
                    <Save className="h-4 w-4 stroke-[2.5]" aria-hidden />
                    <span>Change password</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* The rail: how the new password is doing, and what saving will do. */}
        <aside className="flex flex-col gap-5 xl:col-span-5">
          <section className={cn(PANEL, 'hidden flex-col gap-4 p-5 xl:flex')}>
            <header className="flex items-center gap-2.5">
              <span
                className="relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-sm"
                aria-hidden
              >
                <ShieldCheck className="relative h-4 w-4" />
                <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
              </span>
              <h2 className="min-w-0 flex-1 truncate text-sm font-black tracking-tight text-ink">
                How it is doing
              </h2>
            </header>

            <PasswordStrength value={newPassword} confirmation={confirmPassword} />
          </section>

          <section className={cn(PANEL, 'flex flex-col gap-4 p-5')}>
            <header className="flex items-center gap-2.5">
              <span
                className="relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-sm"
                aria-hidden
              >
                <LogOut className="relative h-4 w-4" />
                <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
              </span>
              <h2 className="min-w-0 flex-1 truncate text-sm font-black tracking-tight text-ink">
                What happens when you save
              </h2>
            </header>

            <ul className="flex flex-col gap-3">
              {CONSEQUENCES.map((item, index) => {
                const Icon = item.icon;

                return (
                  <li
                    key={item.title}
                    className={cn(
                      'flex items-start gap-3 rounded-2xl border border-line bg-surface-2 p-3.5 animate-card-pop',
                      index === 1 && 'delay-100',
                      index === 2 && 'delay-200',
                    )}
                  >
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400"
                      aria-hidden
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12.5px] font-bold text-ink">{item.title}</p>
                      <p className="mt-0.5 text-[11px] font-medium leading-relaxed text-ink-3">
                        {item.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
